import { Plane, MapPin, Briefcase, CalendarDays, Check, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { services, vehicle } from '../data/mock';

const Services = () => {
  const iconComponents = {
    'plane': Plane,
    'palm-tree': MapPin,
    'briefcase': Briefcase,
    'calendar': CalendarDays
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[400px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1731755429012-83a172bec732)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Services</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Professional transportation solutions tailored to your needs
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {services.map((service, index) => {
              const IconComponent = iconComponents[service.icon];
              return (
                <Card key={service.id} className="overflow-hidden border-l-4 border-red-600 hover:shadow-2xl transition-shadow">
                  <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                    <div className={`${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                      <img 
                        src={index === 0 
                          ? 'https://images.unsplash.com/photo-1731755429012-83a172bec732' 
                          : index === 1 
                          ? 'https://images.unsplash.com/photo-1770385801382-723efce550d3'
                          : index === 2
                          ? 'https://images.pexels.com/photos/2026324/pexels-photo-2026324.jpeg'
                          : 'https://images.unsplash.com/photo-1771344152593-388ddb764d74'
                        }
                        alt={service.title}
                        className="w-full h-full object-cover min-h-[300px]"
                      />
                    </div>
                    <CardContent className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="text-3xl font-bold">{service.title}</h2>
                      </div>
                      <p className="text-gray-700 mb-6 text-lg">{service.description}</p>
                      <div className="space-y-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      <Link to="/booking">
                        <Button className="bg-red-600 hover:bg-red-700 text-white w-full md:w-auto px-8">
                          Book This Service
                        </Button>
                      </Link>
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Service Coverage</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card>
                <CardHeader>
                  <CardTitle className="text-red-600">Areas We Serve</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Nassau International Airport
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Paradise Island & Atlantis Resort
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Downtown Nassau
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Cable Beach
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Cruise Port
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      All Hotels & Resorts
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-red-600">What's Included</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Professional, Licensed Drivers
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Clean, Comfortable Vehicles
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Air Conditioning
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Luggage Assistance
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      Flexible Payment Options
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      24/7 Customer Support
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Vehicle Highlight */}
            <Card className="bg-gradient-to-br from-gray-900 to-black text-white border-red-600">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-red-500">{vehicle.name}</h3>
                        <p className="text-gray-400">{vehicle.type}</p>
                      </div>
                    </div>
                    <p className="text-gray-300 mb-6">
                      Travel comfortably with up to 7 passengers in our spacious, well-maintained Honda Step Wagon. 
                      Perfect for families, groups, and airport transfers with plenty of luggage space.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-red-500" />
                        <span>7-Seater Capacity</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-red-500" />
                        <span>Air Conditioned</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-red-500" />
                        <span>Ample Luggage Space</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-red-500" />
                        <span>Comfortable Seating</span>
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 bg-red-600/20 rounded-xl blur-xl"></div>
                    <img 
                      src="https://images.unsplash.com/photo-1623371857133-6d5552bbdc13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxtaW5pdmFufGVufDB8fHx8MTc3NDM5OTczN3ww&ixlib=rb-4.1.0&q=85"
                      alt="Honda Step Wagon"
                      className="relative rounded-xl shadow-2xl w-full"
                      style={{ filter: 'brightness(0.95)' }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Book?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Experience professional transportation service in The Bahamas
          </p>
          <Link to="/booking">
            <Button className="bg-white text-red-600 hover:bg-gray-100 px-10 py-6 text-lg">
              Book Your Ride Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
